<template>
  <div>
    <!-- Per-employee color legend (only when several employees are shown) -->
    <div v-if="legend && legend.length" class="flex flex-wrap gap-x-3 gap-y-1 px-1 pb-2">
      <div v-for="m in legend" :key="m.id" class="flex items-center gap-1.5">
        <span class="w-2.5 h-2.5 rounded-full flex-none" :style="{ background: m.color }" />
        <span class="text-[11px] text-gray-600">{{ m.name }}</span>
      </div>
    </div>

    <!-- Time scale for the occupancy tracks (RTL: morning on the right) -->
    <div class="flex justify-between px-6 pb-1 text-[9.5px] text-gray-400" style="direction: rtl">
      <span dir="ltr">{{ fmtHour(trackRange.start) }}</span>
      <span dir="ltr">{{ fmtHour((trackRange.start + trackRange.end) / 2) }}</span>
      <span dir="ltr">{{ fmtHour(trackRange.end) }}</span>
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-for="day in dayRows"
        :key="day.iso"
        class="bg-white border rounded-xl px-3 py-2.5"
        :class="openIso === day.iso ? 'border-primary-300' : 'border-gray-200'"
      >
        <!-- Header + occupancy track (always visible, tap to expand) -->
        <div class="cursor-pointer" @click="toggle(day.iso)">
          <div class="flex items-center gap-[7px] mb-[7px]">
            <span class="text-[13px] font-semibold">{{ day.label }} · {{ day.dateNum }}</span>
            <span
              v-if="day.isToday"
              class="text-[9.5px] font-semibold text-primary-600 bg-primary-100 rounded-full px-[7px] py-px"
            >היום</span>
            <span class="flex-1" />
            <span
              v-if="day.pending > 0"
              class="text-[10px] font-semibold text-yellow-700 bg-yellow-100 rounded-full px-2 py-0.5"
            >{{ day.pending }} ממתינים</span>
            <span class="text-[11px] text-gray-500">
              {{ day.items.length ? `${day.items.length} תורים · ${fmtHours(day.totalHours)} שע׳` : 'פנוי' }}
            </span>
            <i
              class="pi pi-chevron-down !text-[11px] text-gray-400 transition-transform"
              :class="openIso === day.iso ? 'rotate-180' : ''"
            />
          </div>

          <div class="relative h-[18px] bg-gray-100 rounded-md overflow-hidden">
            <div
              v-for="h in trackTicks"
              :key="h"
              class="absolute inset-y-0 w-px bg-white"
              :style="{ right: pct(h) + '%' }"
            />
            <div
              v-for="block in day.blocks"
              :key="block.key"
              class="absolute opacity-90"
              :style="{
                right: pct(block.start) + '%',
                width: (pct(block.end) - pct(block.start)) + '%',
                top: block.lane * (100 / block.lanes) + '%',
                height: (100 / block.lanes) + '%',
                background: block.color,
              }"
            />
            <div
              v-if="day.isToday && nowHour !== undefined"
              class="absolute inset-y-0 w-[2px] bg-red-500"
              :style="{ right: pct(nowHour) + '%' }"
            />
          </div>
        </div>

        <!-- Expanded: appointment rows + free-window chips + open-in-day-view -->
        <div v-if="openIso === day.iso" class="mt-1.5">
          <div v-if="!day.items.length" class="text-[11.5px] text-gray-400 pt-1.5 pb-0.5">
            אין תורים — יום פנוי להזמנות
          </div>
          <div
            v-for="item in day.items"
            :key="item.event.id"
            class="flex items-center gap-2.5 py-2 border-t border-gray-100 cursor-pointer"
            @click="emit('event', item.event)"
          >
            <div class="w-[42px] flex-none text-center" style="direction: ltr">
              <div class="text-[12px] font-semibold leading-tight">{{ fmtHour(item.start) }}</div>
              <div class="text-[10px] text-gray-400 leading-tight">{{ fmtHour(item.end) }}</div>
            </div>
            <div class="w-[3px] self-stretch rounded-full flex-none" :style="{ background: item.color }" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-1.5 min-w-0">
                <div class="text-[12.5px] font-medium truncate min-w-0">{{ titleText(item.event) }}</div>
                <!-- The list row has the width for labelled chips; the calendar grids don't. -->
                <appointment-markers :event="item.event" variant="pill" />
              </div>
              <div v-if="subText(item.event)" class="text-[11px] text-gray-500 truncate">
                {{ subText(item.event) }}<template v-if="multiStaff && staffNameOf(item.event)"> · {{ staffNameOf(item.event) }}</template>
              </div>
            </div>
            <appointment-status-badge :event="item.event" variant="pill" />
            <i class="pi pi-chevron-left !text-[11px] text-gray-300 flex-none" />
          </div>

          <div v-if="day.gaps.length" class="flex flex-wrap gap-1.5 mt-2">
            <span
              v-for="(gap, i) in day.gaps"
              :key="i"
              class="text-[10.5px] text-gray-600 bg-gray-100 rounded-full px-[9px] py-[3px]"
            >
              פנוי <span dir="ltr">{{ fmtHour(gap[0]) }}–{{ fmtHour(gap[1]) }}</span>
            </span>
          </div>

          <button
            type="button"
            class="w-full flex items-center justify-center gap-1 mt-2 py-[7px] rounded-lg bg-primary-50 text-primary text-[11.5px] font-medium"
            @click="emit('pick-day', day.jsDate)"
          >
            פתיחה בתצוגה יומית
            <i class="pi pi-chevron-left !text-[10px]" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { AppointmentStatus, AppointmentType, type ICalendarEvent } from '@torim-app/types';
import { DateTime } from 'luxon';
import AppointmentMarkers from './AppointmentMarkers.vue';
import AppointmentStatusBadge from './AppointmentStatusBadge.vue';

export interface ICapacityDay {
  iso: string;
  label: string;
  dateNum: number;
  jsDate: Date;
  isToday: boolean;
  events: ICalendarEvent[];
  // Working range of the (single) selected provider — drives the free-window chips.
  workStart?: number;
  workEnd?: number;
}

const props = defineProps<{
  days: ICapacityDay[];
  // Availability-based track window; extended below so out-of-range events still fit.
  trackStart: number;
  trackEnd: number;
  multiStaff: boolean;
  staffNameOf: (e: ICalendarEvent) => string;
  colorOf: (e: ICalendarEvent) => { wrapperBackground: string; lineBg: string; border?: string } | null;
  // When several employees are shown, color the occupancy blocks by employee (so
  // it's obvious who is busy) and render a legend. Single-staff keeps service colors.
  staffColorOf?: (e: ICalendarEvent) => string;
  legend?: Array<{ id: string; name: string; color: string }>;
  nowHour?: number;
}>();
const emit = defineEmits<{ event: [e: ICalendarEvent]; 'pick-day': [d: Date] }>();

const ZONE = 'Asia/Jerusalem';
const MIN_GAP_HOURS = 1;

const parseHM = (s?: string): number => {
  if (!s) return NaN;
  const dt = DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm', { zone: ZONE });
  return dt.isValid ? dt.hour + dt.minute / 60 : NaN;
};

const isManual = (e: ICalendarEvent) => e.type === AppointmentType.MANUAL || e.type === AppointmentType.OUT_OF_OFFICE;
const titleText = (e: ICalendarEvent) => (isManual(e) ? e.title : (e.serviceName || e.title));
const subText = (e: ICalendarEvent) => (isManual(e) ? '' : (e.customerName ?? ''));

const fmtHour = (t: number): string => {
  const h = Math.floor(t);
  const m = Math.round((t - h) * 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};
const fmtHours = (h: number): string => (h % 1 ? (Math.round(h * 10) / 10).toFixed(1) : String(h));

interface DayItem { event: ICalendarEvent; start: number; end: number; color: string }

const itemsOf = (day: ICapacityDay): DayItem[] =>
  day.events
    .map(e => {
      const start = parseHM(e.start);
      let end = parseHM(e.end);
      if (Number.isNaN(end)) end = start + (e.duration ?? 0.5);
      const color = props.staffColorOf?.(e) ?? props.colorOf(e)?.lineBg ?? '#8864ee';
      return { event: e, start, end, color };
    })
    .filter(x => !Number.isNaN(x.start) && x.end > x.start)
    .sort((a, b) => a.start - b.start || a.end - b.end);

// Extend the availability-based window so events outside working hours still fit.
const trackRange = computed(() => {
  let start = props.trackStart;
  let end = props.trackEnd;
  for (const day of props.days) {
    for (const item of itemsOf(day)) {
      start = Math.min(start, Math.floor(item.start));
      end = Math.max(end, Math.ceil(item.end));
    }
  }
  return { start, end };
});
const pct = (t: number): number => {
  const { start, end } = trackRange.value;
  return Math.min(100, Math.max(0, ((t - start) / (end - start)) * 100));
};
const trackTicks = computed(() => {
  const out: number[] = [];
  for (let h = trackRange.value.start + 2; h <= trackRange.value.end - 2; h += 2) out.push(h);
  return out;
});

// Greedy lanes so overlapping appointments split the track height.
const withLanes = (items: DayItem[]) => {
  const laneEnds: number[] = [];
  const placed = items.map(item => {
    let lane = laneEnds.findIndex(end => end <= item.start + 1e-6);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(item.end); } else laneEnds[lane] = item.end;
    return { ...item, key: String(item.event.id), lane };
  });
  return placed.map(p => ({ ...p, lanes: Math.max(1, laneEnds.length) }));
};

// Free windows (≥ MIN_GAP_HOURS) inside the working-hours span that carry no
// appointment. With one employee shown this is their personal free time; with
// several it's when *nobody* is booked. Needs a working-hours span to bound it.
const gapsOf = (day: ICapacityDay, items: DayItem[]): Array<[number, number]> => {
  if (day.workStart === undefined || day.workEnd === undefined) return [];
  const out: Array<[number, number]> = [];
  let cursor = day.workStart;
  for (const item of items) {
    if (item.start - cursor >= MIN_GAP_HOURS) out.push([cursor, item.start]);
    cursor = Math.max(cursor, item.end);
  }
  if (day.workEnd - cursor >= MIN_GAP_HOURS) out.push([cursor, day.workEnd]);
  return out;
};

const dayRows = computed(() => props.days.map(day => {
  const items = itemsOf(day);
  return {
    ...day,
    items,
    blocks: withLanes(items),
    gaps: gapsOf(day, items),
    pending: day.events.filter(e => e.status === AppointmentStatus.WAITING_FOR_BUSINESS_APPROVAL).length,
    totalHours: items.reduce((sum, x) => sum + (x.end - x.start), 0),
  };
}));

// One day open at a time — today (or the first shown day) by default, '' = all closed.
const manualOpen = ref<string>();
const openIso = computed<string>(() => {
  const isos = props.days.map(d => d.iso);
  if (manualOpen.value !== undefined && (manualOpen.value === '' || isos.includes(manualOpen.value)))
    return manualOpen.value;
  return props.days.find(d => d.isToday)?.iso ?? isos[0] ?? '';
});
const toggle = (iso: string) => {
  manualOpen.value = openIso.value === iso ? '' : iso;
};
</script>
